import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Checkbox,
  Typography,
  TextField,
} from "@mui/material"; 
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import theme from "../../themes/index.en";
import StarIcon from "@mui/icons-material/Star";
import { CustomDataTableProps } from "../customDataTable/customDataTableProps";
import CustomDataTableStyles from "../../assets/css/customDataTable.styles";
import { CustomTableColumnsProps } from "../customDataTable/customTableColumnsProps";
import { GeneralStyles } from "../../assets/css/general.styles";

const CustomPaginatedDataTable: React.FC<CustomDataTableProps> = ({
  columns,
  data,
  count,
  rowsPerPageOptions = [5, 10, 25, 100],
  isColored,
  groupByField,
  onPageChange,
  onRowsPerPageChange,
  showCheckbox = false,
  onSelectedItemsChange,
  onSortChange,
  isColoredHeader,
  sortBy,
  sortType,
  onGroupPercentageChange,
  groupPercentages,
  isComparable,
  isCollapsible,
  isSearchable,
  expandedRows,
  expandedRowData,
  handleExpandClick,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set()
  );

  useEffect(() => {
    const selectedItemsList = Array.from(selectedRows).map(
      (index) => data[index]
    );
    if (onSelectedItemsChange) {
      onSelectedItemsChange(selectedItemsList);
    }
  }, [selectedRows, data, onSelectedItemsChange]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    if (onPageChange) {
      onPageChange(newPage + 1);
    }
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    if (onRowsPerPageChange) {
      onRowsPerPageChange(event);
    }
    setPage(0);
  };

  const handleCheckboxChange = (rowIndex: number) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(rowIndex)) {
      newSelectedRows.delete(rowIndex);
    } else {
      newSelectedRows.add(rowIndex);
    }
    setSelectedRows(newSelectedRows);
  };

  const isRowSelected = (rowIndex: number) => selectedRows.has(rowIndex);

  const groupedData = groupByField
    ? data.reduce((acc: { [key: string]: any[] }, item: any) => {
        const key = item[groupByField] || "Ungrouped";
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(item);
        return acc;
      }, {})
    : { "": data };

  const filteredGroupedData = Object.entries(groupedData).reduce(
    (acc, [key, value]) => {
      if (key !== "Ungrouped" || value.length > 0) {
        acc[key] = value;
      }
      return acc;
    },
    {} as { [key: string]: any[] }
  );

  const flattenedData = Object.values(filteredGroupedData).flat();
  const paginatedData = flattenedData.slice(
    page * rowsPerPage,
    (page + 1) * rowsPerPage
  );
  const getGroupName = (index: number) => {
    let count = 0;
    for (const [groupName, items] of Object.entries(filteredGroupedData)) {
      count += items.length;
      if (index < count) return groupName;
    }
    return "";
  };

  const toggleGroupCollapse = (groupName: string) => {
    const newCollapsedGroups = new Set(collapsedGroups);
    if (newCollapsedGroups.has(groupName)) {
      newCollapsedGroups.delete(groupName);
    } else {
      newCollapsedGroups.add(groupName);
    }
    setCollapsedGroups(newCollapsedGroups);
  };

  const getFilteredPaginatedData = () => {
    const result = [];
    let itemCount = 0;

    for (const [groupName, items] of Object.entries(filteredGroupedData)) {
      const isGroupCollapsed = collapsedGroups.has(groupName);

      if (groupName) {
        result.push({ isGroupHeader: true, groupName });
      }

      if (!isGroupCollapsed) {
        for (const item of items) {
          if (
            itemCount >= page * rowsPerPage &&
            itemCount < (page + 1) * rowsPerPage
          ) {
            result.push(item);
          }
          itemCount += 1;
        }
      }

      if (itemCount >= (page + 1) * rowsPerPage) break;
    }

    return result;
  };

  const filteredPaginatedData = getFilteredPaginatedData();

  return (
    <Paper sx={CustomDataTableStyles.paper}>
      <TableContainer
        sx={{ ...CustomDataTableStyles.tableContainer, overflowX: "auto" }}
      >
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {showCheckbox && (
                <TableCell
                  padding="checkbox"
                  sx={{
                    ...(isColoredHeader
                      ? CustomDataTableStyles.tableHeadCell
                      : CustomDataTableStyles.paginatedTableHeadCell),
                    ...CustomDataTableStyles.stickyHeader,
                    left: 0,
                  }}
                >
                  <Checkbox
                    style={GeneralStyles.checkBoxStyle}
                    indeterminate={
                      selectedRows.size > 0 && selectedRows.size < data.length
                    }
                    checked={selectedRows.size === data.length}
                    onChange={() => {
                      const newSelectedRows = new Set(
                        selectedRows.size === data.length
                          ? []
                          : data.map((_, index) => index)
                      );
                      setSelectedRows(newSelectedRows);
                    }}
                  />
                </TableCell>
              )}
              {isComparable && (
                <TableCell
                  sx={
                    isColoredHeader
                      ? CustomDataTableStyles.tableHeadCell
                      : CustomDataTableStyles.paginatedTableHeadCell
                  }
                >
                  {" "}
                </TableCell>
              )}
              {columns
                .filter((col) => col.visible !== false)
                .map((column: CustomTableColumnsProps, index) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    sx={
                      column.id === "primaryImageUrl"
                        ? {
                            ...(isColoredHeader
                              ? CustomDataTableStyles.tableHeadCell
                              : CustomDataTableStyles.paginatedTableHeadCell),
                            ...CustomDataTableStyles.stickyHeader,
                            left: showCheckbox ? 40 : 0,
                          }
                        : isColoredHeader
                          ? CustomDataTableStyles.tableHeadCell
                          : CustomDataTableStyles.paginatedTableHeadCell
                    }
                    onClick={() => onSortChange && onSortChange(column.id)}
                  >
                    {column.label}
                    {sortBy === column.id &&
                      (sortType === "asc" ? (
                        <ArrowDropUpIcon />
                      ) : (
                        <ArrowDropDownIcon />
                      ))}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredPaginatedData.length === 0 ? (
              <TableRow sx={CustomDataTableStyles.paginatedTableRow}>
                <TableCell
                  colSpan={columns.length + (showCheckbox ? 1 : 0)}
                  align="center"
                >
                  <Typography variant="body1">No data available</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPaginatedData.map((row: any, rowIndex: number) => {
                if (row.isGroupHeader) {
                  const isGroupCollapsed = collapsedGroups.has(row.groupName);

                  return (
                    <TableRow
                      key={`group-${row.groupName}`}
                      sx={CustomDataTableStyles.groupHeaderRow}
                    >
                      <TableCell
                        colSpan={
                          isComparable
                            ? columns.length - 3 + (showCheckbox ? 1 : 0)
                            : columns.length - 4 + (showCheckbox ? 1 : 0)
                        }
                        sx={{
                          ...CustomDataTableStyles.groupHeaderCell,
                        }}
                      >
                        {isCollapsible ? (
                          <span
                            onClick={() => toggleGroupCollapse(row.groupName)}
                            style={{
                              cursor: "pointer",
                              display: "flex",
                              alignItems: "center",
                            }}
                          >
                            {isGroupCollapsed ? (
                              <KeyboardArrowRightIcon />
                            ) : (
                              <KeyboardArrowDownIcon />
                            )}
                            <span>{row.groupName}</span>
                          </span>
                        ) : (
                          <span>{row.groupName}</span>
                        )}
                      </TableCell>
                      {onGroupPercentageChange && (
                        <TableCell
                          sx={{
                            textAlign: "right",
                          }}
                        >
                          <TextField
                            variant="outlined"
                            size="small"
                            placeholder="Percentage"
                            value={
                              (groupPercentages &&
                                groupPercentages[row.groupName]) ||
                              ""
                            }
                            onChange={(e: any) =>
                              onGroupPercentageChange &&
                              onGroupPercentageChange(
                                row.groupName,
                                e.target.value
                              )
                            }
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  );
                }

                const globalIndex = page * rowsPerPage + rowIndex;
                const isExpanded = expandedRows?.has(
                  row.privateLabelItemManufacturerId ||
                    row.oilManufacturerId ||
                    row.batteryManufacturerId
                );
                const comparableItems =
                  expandedRowData?.[
                    row.privateLabelItemManufacturerId ||
                      row.oilManufacturerId ||
                      row.batteryManufacturerId
                  ] || [];

                return (
                  <React.Fragment key={globalIndex}>
                    <TableRow
                      key={`${rowIndex}`}
                      sx={{
                        ...CustomDataTableStyles.paginatedTableRow,
                        backgroundColor:
                          (isSearchable && ((row.manufacturerCountry !== null && row.price === 0) || row.manufacturerCountry === null))
                            ? theme.palette.warning.light
                            : theme.palette.background.default,
                      }}
                    >
                      {showCheckbox && (
                        <TableCell
                          padding="checkbox"
                          sx={{
                            ...CustomDataTableStyles.stickyColumn,
                            left: 0,
                          }}
                        >
                          <Checkbox
                            checked={isRowSelected(globalIndex)}
                            style={GeneralStyles.checkBoxStyle}
                            onChange={() => handleCheckboxChange(globalIndex)}
                          />
                        </TableCell>
                      )}
                      {isComparable && (
                        <TableCell>
                          {isExpanded ? (
                            <KeyboardArrowDownIcon
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleExpandClick && handleExpandClick(row)
                              }
                            />
                          ) : (
                            <KeyboardArrowRightIcon
                              style={{ cursor: "pointer" }}
                              onClick={() =>
                                handleExpandClick && handleExpandClick(row)
                              }
                            />
                          )}
                        </TableCell>
                      )}
                      {columns
                        .filter((col) => col.visible !== false)
                        .map((column: CustomTableColumnsProps) => (
                          <TableCell
                            key={column.id}
                            align={column.align}
                            sx={{
                              ...(column.id === "primaryImageUrl"
                                ? {
                                    ...CustomDataTableStyles.stickyColumn,
                                    left: showCheckbox ? 40 : 0,
                                  }
                                : {}),
                              color:
                                row.quantity !== "" &&
                                row.quantity !== undefined &&
                                row.quantity !== 0 &&
                                row.moq > row.quantity
                                  ? theme.palette.error.main
                                  : undefined,
                            }}
                          >
                            {column.renderCell
                              ? column.renderCell({ row })
                              : row[column.id]}
                            {column.id == "manufacturerCountry" &&
                              row.isEuropeOriginCountry && (
                                <StarIcon
                                  sx={{ color: theme.palette.warning.main }}
                                />
                              )}
                          </TableCell>
                        ))}
                    </TableRow>

                    {isExpanded && (
                      <>
                        <TableRow
                          key={`child-${globalIndex}`}
                          sx={{ backgroundColor: theme.palette.info.dark }}
                        >
                          <TableCell></TableCell>
                          <TableCell>Brand</TableCell>
                          <TableCell>Code</TableCell>
                          <TableCell colSpan={columns.length - 3}>
                            Description
                          </TableCell>
                          <TableCell>Price</TableCell>
                        </TableRow>
                        {comparableItems.map(
                          (childRow: any, childIndex: number) => (
                            <TableRow
                              key={`child-${globalIndex}-${childIndex}`}
                              sx={{
                                ...CustomDataTableStyles.paginatedTableRow,
                              }}
                            >
                              <TableCell></TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    childRow.price < row.cost
                                      ? theme.palette.error.contrastText
                                      : "",
                                }}
                              >
                                {childRow.brand}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    childRow.price < row.cost
                                      ? theme.palette.error.contrastText
                                      : "",
                                }}
                              >
                                {childRow.code}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    childRow.price < row.cost
                                      ? theme.palette.error.contrastText
                                      : "",
                                }}
                                colSpan={columns.length - 3}
                              >
                                {childRow.description}
                              </TableCell>
                              <TableCell
                                sx={{
                                  color:
                                    childRow.price < row.cost
                                      ? theme.palette.error.contrastText
                                      : "",
                                }}
                              >
                                {childRow.price
                                  ? `$${childRow.price.toFixed(2)}`
                                  : "0"}
                              </TableCell>
                            </TableRow>
                          )
                        )}
                      </>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={rowsPerPageOptions}
        component="div"
        count={flattenedData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={CustomDataTableStyles.tablePagination}
      />
    </Paper>
  );
};

export default CustomPaginatedDataTable;
